import { useState } from 'react';
import { Button, Card, Group, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconArchive, IconCloudDownload, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api-client';
import { env } from '../../lib/env';
import { useAuthStore } from '../../auth/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import type { Project } from '../../types/api';
import { ApiError } from '../../lib/api-error';

interface Props {
  project: Project;
  canEdit: boolean;
  canDelete: boolean;
  canArchive: boolean;
}

const STATUS_OPTIONS: { value: Project['status']; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'closed', label: 'Closed' },
];

export function ProjectSettingsTab({ project, canEdit, canDelete, canArchive }: Props) {
  const [name, setName] = useState(project.name);
  const [company, setCompany] = useState(project.company_name);
  const [description, setDescription] = useState(project.description ?? '');
  const [status, setStatus] = useState<Project['status']>(project.status);
  const [submitting, setSubmitting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);

  /**
   * Streams /projects/:id/archive into a Blob and triggers the browser's
   * Save dialog. Uses the in-memory access token (the API expects Bearer
   * auth). Memory cost is bounded by the actual archive size; for our
   * project sizes that's fine.
   */
  async function downloadArchive() {
    setArchiving(true);
    try {
      if (!accessToken) throw new Error('not_signed_in');
      // The /projects/:id/archive endpoint is wrapped in withProjectContext,
      // which requires X-Project-Id (or rt_project cookie). Other API calls
      // get this header injected by api-client.ts; this hand-rolled fetch
      // must add it explicitly or the server returns 403 project_id_required.
      const res = await fetch(`${env.apiBaseUrl}/projects/${project.id}/archive`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Project-Id': project.id,
        },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`archive_failed_${res.status}: ${text.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const filenameMatch = /filename="?([^";]+)"?/.exec(cd);
      const filename = filenameMatch?.[1] ?? `${project.name}-archive.zip`;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      notifications.show({
        title: 'Archive downloaded',
        message: filename,
        color: 'pine',
      });
    } catch (err) {
      notifications.show({
        title: 'Archive failed',
        message: err instanceof Error ? err.message : 'Unexpected error',
        color: 'red',
      });
    } finally {
      setArchiving(false);
    }
  }

  async function save() {
    setSubmitting(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        name,
        company_name: company,
        description: description || null,
        status,
      });
      await qc.invalidateQueries({ queryKey: ['projects'] });
      await qc.invalidateQueries({ queryKey: ['admin-projects'] });
      notifications.show({ title: 'Project updated', message: name, color: 'pine' });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDelete() {
    modals.openConfirmModal({
      title: 'Soft-delete this project?',
      children: (
        <Text size="sm">
          The project will disappear from listings but audit history is preserved. Members and their
          work stay in the database for administrative recovery.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/projects/${project.id}`);
          await qc.invalidateQueries({ queryKey: ['projects'] });
          await qc.invalidateQueries({ queryKey: ['admin-projects'] });
          notifications.show({
            title: 'Project deleted',
            message: project.name,
            color: 'pine',
          });
          navigate('/admin/projects');
        } catch (err) {
          notifications.show({
            title: 'Delete failed',
            message: err instanceof ApiError ? err.message : 'Unexpected error',
            color: 'red',
          });
        }
      },
    });
  }

  return (
    <Stack gap="lg">
      <Card withBorder padding="lg">
        <Title order={4} mb="sm">
          Details
        </Title>
        <Stack gap="sm">
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            disabled={!canEdit}
          />
          <TextInput
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.currentTarget.value)}
            disabled={!canEdit}
          />
          <TextInput
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            disabled={!canEdit}
          />
          <Select
            label="Status"
            data={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setStatus((v ?? 'active') as Project['status'])}
            disabled={!canEdit}
            leftSection={<IconArchive size={14} />}
          />
          <Group justify="flex-end">
            <Button onClick={save} loading={submitting} disabled={!canEdit}>
              Save changes
            </Button>
          </Group>
        </Stack>
      </Card>

      {canArchive && (
        <Card withBorder padding="lg">
          <Title order={4} mb="sm">
            Export
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Download every PDF and image attached to this project, organized as{' '}
            <code>tract / title-run / file.ext</code>. A single document referenced from multiple
            entries is copied into each folder so the archive is self-contained.
          </Text>
          <Button
            leftSection={<IconCloudDownload size={14} />}
            onClick={downloadArchive}
            loading={archiving}
          >
            Download project archive
          </Button>
        </Card>
      )}

      {canDelete && (
        <Card withBorder padding="lg">
          <Title order={4} mb="sm" c="red">
            Danger zone
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Soft-deleting the project hides it everywhere. You can still recover it at the database
            level.
          </Text>
          <Button
            color="red"
            variant="light"
            leftSection={<IconTrash size={14} />}
            onClick={confirmDelete}
          >
            Delete project
          </Button>
        </Card>
      )}
    </Stack>
  );
}
